<template>

    <div class="pt-3">
        
        <v-card tile elevation="0" class="pa-0 ma-0">
            <v-card-title class="pa-0 ma-0">
                <FormsTextField 
                    v-model="textSearch"
                    label="Pesquisar"
                    append-icon="mdi-magnify"
                    outlined
                    dense
                    placeholder="Comece a digitar para pesquisar"
                    v-if="searchable"
                    :style="breakpoint().name === 'xs' ? 'width: 100%' : 'width: 30%'"
                />
                <v-spacer></v-spacer>
                <emc-table-icon v-if="showReload" :loading="isLoading" icon="mdi-refresh" color="success" tool-tip="Atualizar" @click="reloadData()" />
                <slot name="tools"></slot>
            </v-card-title>
        </v-card>
        
        <v-data-table
            :headers="headersItems"
            :items="collection"
            :options.sync="options"
            :server-items-length="meta.total"
            :page.sync="meta.page"
            :items-per-page="options.itemsPerPage"
            :footer-props="footerProps"
            :loading="isLoading"
            :no-data-text="(textSearch || !searchable) ? `Nenhum resultado encontrado` : `Faça uma pesquisa para visualizar os dados...`"
            loading-text="Carregando..."
        >

            <!-- Esse trecho faz com que o componente pai possa usar qualquer slot do v-data-table -->
            <template v-for="slot in Object.keys($scopedSlots)" :slot="slot" slot-scope="scope">
                <slot :name="slot" v-bind="scope"/>
            </template>

            <!-- Formatação de campo caso seja passado o type -->
            <template v-for="(header, index) in headerItemsTypes" v-slot:[`item.${header.value}`]="{ item }">
                <TablesPartFormatItem :header="header" :item="item" :key="index"/>
            </template>

            <!-- Trata os itens que aparecem na coluna "actions" do data table -->
            <template v-slot:item.actions="{ item }">
                <tables-part-actions 
                    :no-default-actions="noDefaultActions"
                    :hide-btn-edit="hideBtnEdit"
                    :hide-btn-delete="hideBtnDelete"
                    @clickedEditButton="edit(item)" 
                    @clickedDeleteButton="confirmation(item)"
                >
                    <template v-slot:actions>
                        <slot name="actions" :item="item"></slot>
                    </template>
                </tables-part-actions>
            </template>

            <!-- Exibe o campo padrão caso exista a coluna "active" no item -->
            <template v-slot:item.active="{ item }">
                <TablesPartActive :item="item" />
            </template>

            <template v-slot:footer.prepend v-if="onlyActive">
                <v-row>
                    <FormsSwitch v-model="filterIsActive" sm="3" class="ml-3" label="Exibir apenas itens ativos" />
                </v-row>
            </template>

        </v-data-table>

        <DialogsConfirmation 
            :show.sync="showConfirmation" 
            message="Tem certeza que deseja excluir?" 
            @confirm="deleteItem"
            :loading="isLoading"
        />
    </div>
    
</template>

<script setup>

import { ref, defineProps, computed, watch, defineEmits } from 'vue';
import { useStore } from '@/composables/useStore'
import { breakpoint } from '@/composables/breakpoint';

import debounce from 'lodash/debounce'

const emit = defineEmits(['clickedEditButton', 'update:items', 'update:reload', 'loaded'])

const props = defineProps({
    headers: { type: Array, required: true },
    items: { type: Array, required: false },
    storeAction: { type: String, required: false },
    storeActionDelete: { type: String, required: false },
    loadData: { type: Boolean, default: false },
    params: { type: Object, default: () => ({}) },
    reload: { type: Boolean, default: false },
    searchable: { type: Boolean, default: true },
    noDefaultActions: { type: Boolean, default: false },
    hideBtnEdit: { type: Boolean, default: false },
    hideBtnDelete: { type: Boolean, default: false },
    onlyActive: { type: Boolean, default: false },
    showReload: { type: Boolean, default: false },
    // Opcoes do seletor de itens por pagina.
    itemsPerPageOptions: { type: Array, default: () => [ 5, 10, 15, 30] },
})

const options = ref({ itemsPerPage: 5})
const footerProps = computed(() => ({ 'items-per-page-options': props.itemsPerPageOptions }))
const meta = ref({})
const textSearch = ref('')
const isLoading = ref(false)
const showConfirmation = ref(false)
const itemDelete = ref({})
const dataItems = ref([])

const filterIsActive = ref(true)

const headersItems = computed(() => {

    //Definindo regras padrão para alguns tipos de colunas
    let headers = props.headers.map(item => {

        //A coluna de botões de ações não poderá ser ordenada
        if(item.value === 'actions') {

            return {
                ...item,
                align: 'center',
                sortable: false
                
            }
        }

        if(item.type === 'boolean' || item.value === 'active') {
            return {
                ...item,
                align: 'center'
            }
        }

        //Nenhum item com relação será ordenado
        if(item.value.includes('.')) {
            return {
                ...item,
                sortable: false
            }
        }

        return item
    })

    return headers
})

const headerItemsTypes = computed(() => {
    return props.headers.filter((header) => header.type)
})

const collection = computed({
    get() {
        return props.items ?? dataItems.value
    },
    set(value) {
        emit('update:items', value)
    }
})

const store = useStore()
const ActionRequest = (params) => store.dispatch(props.storeAction, params)
const ActionRequestDelete = (params) => store.dispatch(props.storeActionDelete, params)

//Função para deletar o item
function deleteItem() {

    isLoading.value = true

    const params = {
        id: itemDelete.value.id,
    }

    ActionRequestDelete(params)
        .then(res => {
            const params = setParams()
            getData(params)
        })
        .catch(() => {
            //Tratar erro
        })
        .finally(() => {
            isLoading.value = false
            showConfirmation.value = false
        })
}

//Função para buscar os dados
function getData(params) {

    isLoading.value = true

    const filters = {
        ...params
    }

    dataItems.value = [];

    ActionRequest(filters)
        .then(res => {
            dataItems.value = res.data
            meta.value = res.meta
            emit('loaded')
        })
        .finally(() => {
            isLoading.value = false
        })
}

//Define todos os parâmetros que serão passados para a requisição retornar os dados
function setParams() {

    const columnOrder  = options.value.sortBy.length ? options.value.sortBy[0] : null;
    const direction     = (options.value.sortDesc.length && options.value.sortDesc[0]) ? 'desc' : 'asc'

    let params = {
        search: textSearch.value,
        column_order: columnOrder,
        direction: direction,
        page: options.value.page,
        per_page: options.value.itemsPerPage,
        ...props.params
    }

    //Adicionando parâmetro para buscar somente itens ativos
    if (props.onlyActive) {
        params = {
            ...params,
            find_columns: {
                active: filterIsActive.value ? 1 : 0,
            }
        }
    }

    return params
}

// Retorna qual item será editado para o componente pai
function edit(item) {

    const itemEdit = { ...item }

    emit('clickedEditButton', itemEdit)
}

// Executa a confirmação em caso de exclusão de um item
function confirmation(item) {
    itemDelete.value = {} //Garantindo a limpeza do item correto
    showConfirmation.value = true
    itemDelete.value = item
}

function reloadData() {
    const params = setParams()
    getData(params)
}

// Executando a ação de pesquisa baseado nos parâmetros de busca
const debouncedSearch = debounce((newVal, oldVal) => {
  
    dataItems.value = []
    
    if (oldVal.length === 0 && !props.loadData) return

    if (newVal.length <= 2 && !props.loadData) return

    // Uma nova pesquisa sempre volta para a primeira pagina (senao, estando na
    // pagina 3, um resultado menor apareceria vazio).
    const changedPage = options.value.page > 1
    if (changedPage) {
        options.value = { ...options.value, page: 1 }

        // Com loadData, o watch de options recarrega a lista.
        if (props.loadData) return
    }

    const params = setParams()

    getData(params)

}, 500)

// Buscando novos dados quando a pesquisa for preenchida
watch(textSearch, (newVal, oldVal) => {
  debouncedSearch(newVal, oldVal)
})

// Sempre que o options do data table ou a propriedade reload que é passada for alterada, os dados serão recarregados
watch([options, () => props.reload], () => {

    const params = setParams()

    /**
     * Recarregar a lista somente para componentes
     * que possuem o parâmetro load data
     */
    if (props.loadData || (props.reload && props.loadData) ) {
        getData(params)
        emit('update:reload', false)
    }

})

watch(filterIsActive, () => {

    const params = setParams()

    getData(params)

})

watch(() => props.params, (newVal) => {
    
    if(newVal === null || newVal === undefined) return

    // Novos parametros (ex.: filtros) sempre voltam para a primeira pagina.
    if (options.value.page > 1) {
        options.value = { ...options.value, page: 1 }

        // Com loadData, o watch de options recarrega a lista.
        if (props.loadData) return
    }

    const params = setParams()
    getData(params)
})

</script>
